/**
 * Demo AI jobs and the proposals a simulated job leaves behind (?mock=1). The proposals are
 * canned but follow the request: an import proposes the sections it was given, a draft from a
 * screenshot becomes a defect, a split proposes smaller stories.
 */
import { AI_DEFAULT_SETTINGS, newPrefixedId } from '@orgus/protocol';
import type { AcceptanceInput, AiJob, AiJobsState, AiTask, BacklogItem, DefectSignal, Id, ItemKind, MissionsState, Proposal, ProposedItem, SourceSection } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';

const MIN = 60_000;

function job(id: string, task: AiTask, title: string, extra: Partial<AiJob>, now: number): AiJob {
  return {
    id,
    task,
    title,
    projectId: MOCK_IDS.billing,
    itemId: null,
    machineId: MOCK_IDS.studio,
    model: 'sonnet',
    state: 'done',
    stage: 'finished',
    by: 'srikanth',
    createdAt: now - 10 * MIN,
    startedAt: now - 10 * MIN,
    endedAt: now - 9 * MIN,
    cost: 0.0841,
    tokens: { input: 12_400, output: 3_100 },
    turns: 1,
    events: 24,
    outputChars: 6200,
    expectedChars: 6000,
    error: null,
    proposalId: null,
    summary: null,
    ...extra,
  };
}

export function mockAiJobs(now: number): AiJobsState {
  return {
    settings: { ...AI_DEFAULT_SETTINGS },
    jobs: [
      job('job_demo_running', 'import', 'Import onboarding-spec.docx', { projectId: MOCK_IDS.orgus, state: 'running', stage: 'writing', createdAt: now - 48_000, startedAt: now - 48_000, endedAt: null, cost: null, tokens: null, turns: null, outputChars: 3900, expectedChars: 7200, events: 31 }, now),
      // Waits on studio while Qawl lands there (the project protocol's arbitration demo, project-health/mock.ts).
      job('job_demo_summary', 'split', 'Split: Self-serve machine onboarding', { projectId: MOCK_IDS.orgus, state: 'queued', stage: 'sending', createdAt: now - 5 * MIN, startedAt: null, endedAt: null, cost: null, tokens: null, turns: null }, now),
      job('job_demo_import_done', 'import', 'Import payments-spec.md', { proposalId: 'prp_demo_import', summary: '2 epics, 2 features, 4 stories proposed', createdAt: now - 6 * MIN, startedAt: now - 6 * MIN, endedAt: now - 4 * MIN, cost: 0.1137 }, now),
      job('job_demo_failed', 'draft', 'Draft: export to the finance system', {
        projectId: MOCK_IDS.billing,
        machineId: MOCK_IDS.build,
        state: 'failed',
        cost: null,
        tokens: null,
        error: { what: 'Draft item failed.', why: 'Claude Code exited with code 1: Not logged in. Run claude and sign in.', next: 'Sign in to Claude Code on that machine (run claude once), then try again.' },
        createdAt: now - 40 * MIN,
        startedAt: now - 40 * MIN,
        endedAt: now - 40 * MIN + 4000,
      }, now),
    ],
  };
}

const gwt = (given: string, when: string, then: string): AcceptanceInput => ({ format: 'gwt', text: '', given, when, then });

function proposed(ref: string, parentRef: string, kind: ItemKind, title: string, body: string, extra: Partial<ProposedItem> = {}): ProposedItem {
  return { ref, parentRef, kind, title, body, acceptance: [], priority: 2, estimate: kind === 'story' ? 3 : null, labels: [], defect: null, sections: [], decision: 'pending', ...extra };
}

function base(kind: Proposal['kind'], projectId: Id, title: string, extra: Partial<Proposal>): Proposal {
  const now = Date.now();
  return { id: newPrefixedId('prp', now), kind, projectId, title, by: 'owner', createdAt: now, updatedAt: now, jobId: null, status: 'open', items: [], edits: [], links: [], questions: [], assumptions: [], source: null, targetItemId: null, ...extra };
}

/** What a simulated job proposes. */
export function mockProposalFor(task: AiTask, projectId: Id, request: string, sections: readonly SourceSection[], item: BacklogItem | null, state: MissionsState, kind: ItemKind | null = null, signal?: DefectSignal): Proposal | null {
  switch (task) {
    case 'import': {
      const items: ProposedItem[] = [];
      const tops = sections.filter((s) => s.level === 1);
      (tops.length ? tops : sections.slice(0, 1)).forEach((s, n) => {
        const e = `E${n + 1}`;
        items.push(proposed(e, '', 'epic', s.title, s.text.split('\n')[0] ?? '', { sections: [s.id] }));
        for (const sub of sections.filter((x) => x.id.startsWith(`${s.id}.`))) {
          items.push(proposed(`S${items.length}`, e, 'story', sub.title, `As a customer, I want ${sub.title.toLowerCase()}, so that it works the way the spec says.`, { sections: [sub.id], acceptance: [gwt('the situation the section describes', 'it happens', sub.text.split('.')[0]?.toLowerCase() || 'it behaves as described')] }));
        }
      });
      return base('import', projectId, `Import of ${request}`, { items, questions: ['Which roles may do this? The document does not say.'], assumptions: ['Prices include tax.'], source: { name: request, sections: [...sections], chars: sections.reduce((n, s) => n + s.text.length, 0) } });
    }
    case 'draft': {
      const defect = kind === 'defect' || /broken|does not|doesn't|error|fail|crash|screenshot/i.test(request);
      const it = defect
        ? proposed('D1', '', 'defect', request ? request[0]!.toUpperCase() + request.slice(1) : 'Login button does nothing on Safari', 'Seen in the attached screenshot. The page stays on the form and no error is shown.', {
            priority: 1,
            defect: { severity: 'major', environment: 'Safari 18 on macOS 15', steps: '1. Open /login\n2. Enter a valid email and password\n3. Click Sign in', expected: 'You are signed in and land on the home page.', actual: 'Nothing happens; the button shows no progress.' },
            acceptance: [gwt('a valid account on Safari 18', 'I click Sign in', 'I land on the home page within 2 seconds')],
          })
        : proposed('S1', '', kind ?? 'story', request ? request[0]!.toUpperCase() + request.slice(1) : 'New story', `As a user, I want ${request || 'this'}, so that my work is faster to finish.`, {
            acceptance: [gwt('the feature is on', 'I use it', 'the result appears within 1 second'), gwt('the input is invalid', 'I submit', 'I see what is wrong and how to fix it')],
          });
      return base('draft', projectId, `Draft: ${request || 'from attachments'}`, { items: [it] });
    }
    case 'defect': {
      return base('defect', projectId, `Defect: ${request}`, {
        items: [proposed('D1', '', 'defect', request.replace(/^Session ended on an error: /, ''), 'What the evidence shows, and the likely cause.', { priority: 1, defect: { severity: 'major', environment: 'build-01', steps: '1. Run the same command\n2. Read the output', expected: 'It passes.', actual: signal?.evidence.split('\n').pop() ?? 'It fails.' } })],
        ...(signal ? { signalId: signal.id } : {}),
      });
    }
    case 'split': {
      if (!item) return null;
      return base('split', projectId, `Split of ${item.key} into 3`, {
        targetItemId: item.id,
        dropOriginal: item.kind === 'story',
        items: [
          proposed('S1', '', 'story', `${item.title}: the main path`, 'The smallest slice that delivers value.', { parentId: item.parentId, estimate: 3, acceptance: [gwt('the common case', 'I do it', 'it works end to end')] }),
          proposed('S2', '', 'story', `${item.title}: exports`, 'Export what the first slice shows.', { parentId: item.parentId, estimate: 3 }),
          proposed('S3', '', 'story', `${item.title}: per team and per person`, 'Group and filter.', { parentId: item.parentId, estimate: 5 }),
        ],
      });
    }
    case 'improve':
    case 'criteria':
    case 'estimate': {
      if (!item) return null;
      const patch =
        task === 'estimate'
          ? { estimate: 5 }
          : task === 'criteria'
            ? { acceptance: [...item.acceptance.map((c) => ({ id: c.id, format: c.format, text: c.text, given: c.given, when: c.when, then: c.then, done: c.done })), gwt('an empty inbox', 'I open it', 'I see "Nothing is waiting for you" and no spinner'), gwt('200 waiting requests', 'I open the inbox', 'the first 50 show within 1 second')] }
            : { title: item.title, body: `${item.body}\n\nMeasured as: the inbox shows within 1 second with 200 waiting requests.`.trim(), acceptance: item.acceptance.map((c) => (/fast/i.test(c.text) ? { format: 'gwt' as const, text: '', given: '200 waiting requests', when: 'I open the inbox', then: 'the first 50 show within 1 second' } : { id: c.id, format: c.format, text: c.text, given: c.given, when: c.when, then: c.then, done: c.done })) };
      const rationale = task === 'estimate' ? 'Similar in size to QAWL-3 (5 points); the routing is new. Confidence: medium.' : task === 'criteria' ? 'Covers the empty state and a large inbox.' : 'Replaced "fast" with a time limit a test can check.';
      return base('edit', projectId, task === 'estimate' ? `Estimate for ${item.key}: 5 points` : task === 'criteria' ? `2 acceptance criteria for ${item.key}` : `Improvements to ${item.key}`, { targetItemId: item.id, edits: [{ itemId: item.id, patch, rationale, decision: 'pending' }] });
    }
    case 'dedupe': {
      const open = state.items.filter((i) => i.projectId === projectId && i.status !== 'done' && i.status !== 'dropped');
      const pairs: Proposal['links'] = [];
      for (const a of open) for (const b of open) if (a.id < b.id && a.title.replace(/\bon\b|\bfor\b/g, '').slice(0, 14) === b.title.replace(/\bon\b|\bfor\b/g, '').slice(0, 14)) pairs.push({ from: b.id, to: a.id, type: 'duplicates', rationale: 'Both ask for the same change in other words.', decision: 'pending' });
      return base('links', projectId, `Possible duplicates (${pairs.length})`, { links: pairs });
    }
    // Orgus's review of finished work (ORGU-85) moves the item itself: it proposes nothing.
    case 'review':
    case 'handoff':
      // A session handoff (ADR 0062 decision 2a) is kept on its mission, never proposed.
      return null;
  }
}
